// ============================
// ✅ AppartementPlan corrigé
// ============================
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";

import {
  toDate,
  isToday,
  diffDays
} from "../utils/dateUtils";

import {
  isExpired,
  isUrgentToday,
  isUrgentJ1
} from "../utils/urgenceUtils";

function AppartementPlan() {
  const [appartements, setAppartements] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [modeUrgence, setModeUrgence] = useState(false);

  const alertedRef = useRef({});
  const expiredAlertedRef = useRef({});

  const navigate = useNavigate();

  const getEtat = (app) => {
    return (
      app.statut_reel || 
      app.etat_reel || 
      app.etat || 
      ""
    ).toLowerCase();
  };

  const getRemainingToNoon = () => {
    const now = new Date();
    const noon = new Date();
    noon.setHours(12, 0, 0, 0);

    const diff = noon - now;
    if (diff <= 0) return null;

    const h = Math.floor(diff / (1000 * 60 * 60));
    const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${h}h ${m}m`;
  };

  const showCheckin = (app) => {
    const now = new Date();

    return (
      getEtat(app) === "occupe" &&
      isToday(app.date_debut) &&
      now.getHours() >= 6 &&
      now.getHours() < 12
    );
  };

  const showCheckoutActions = (app) => {
    const now = new Date();

    return (
      getEtat(app) === "occupe" &&
      isToday(app.date_fin_prevue) &&
      now.getHours() >= 6 &&
      now.getHours() < 12
    );
  };

  const isUrgentJ1 = (app) => {
    const days = diffDays(toDate(app.date_fin_prevue), new Date());
    return app.reste_a_payer > 0 && days === 1;
  };

  const isUrgentToday = (app) => {
    if (!app.date_fin_prevue) return false;

    const fin =
      typeof app.date_fin_prevue === "string"
        ? toDate(app.date_fin_prevue)
        : new Date(app.date_fin_prevue);

    return (
      getEtat(app) === "occupe" &&
      app.reste_a_payer > 0 &&
      isToday(fin)
    );
  };

  const isUrgent = (app) => {
    return (
      isExpired(app) ||
      isUrgentToday(app) ||
      isUrgentJ1(app)
    );
  };

  const handleCheckin = async (app, e) => {
    e.stopPropagation();
    await api.post(`/appartements/checkin/${app.sejour_id}`);
    loadData();
  };

  const handleExtend = (app, e) => {
    e.stopPropagation();
    localStorage.setItem("selectedAppartement", JSON.stringify(app));
    navigate("/appartement");
  };

  const handleCheckout = async (app, e) => {
    e.stopPropagation();
    await api.post(`/sejours/terminer/${app.sejour_id}`);
    loadData();
  };

  const playAlert = () => {
    new Audio("https://actions.google.com/sounds/v1/alarms/beep_short.ogg")
      .play()
      .catch(() => {});
  };

  const playExpiredAlert = () => {
    new Audio("https://actions.google.com/sounds/v1/alarms/alarm_clock.ogg")
      .play()
      .catch(() => {});
  };

  const loadData = async () => {
    const res = await api.get("/appartements");
    const data = res.data;

    setAppartements(data);

    data.forEach(app => {
      if ((isUrgentJ1(app) || isUrgentToday(app)) && !alertedRef.current[app.id]) {
        alertedRef.current[app.id] = true;
        setAlerts(prev => [
          ...prev,
          { id: "urgent-" + app.id, message: `⚠️ À solder - ${app.numero}` }
        ]);
        playAlert();
      }

      if (isExpired(app) && !expiredAlertedRef.current[app.id]) {
        expiredAlertedRef.current[app.id] = true;
        setAlerts(prev => [
          ...prev,
          { id: "expired-" + app.id, message: `🚨 Séjour expiré - ${app.numero}` }
        ]);
        playExpiredAlert();
      }
    });

      console.log("DATA APPARTEMENTS:", data);
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  const getColor = (app) => {
    const etat = getEtat(app);

    // 🔥 OCCUPATION PRIORITAIRE
    if (etat === "occupe") return "bg-red-500";
    if (etat === "reserve") return "bg-yellow-500";

    // 🔥 LIBRES PAR TYPE
    if (etat === "libre") {

      if (app.type_logement === "chambre") {
        return "bg-cyan-600";
      }

      if (app.type_logement === "appartement premium") {
        return "bg-purple-600";
      }

      if (app.type_logement === "appartement standard") {
        return "bg-green-600";
      }

      return "bg-green-500";
    }

    return "bg-gray-400";
  };

  const getDuree = (date) => {
    const start = toDate(date);
    if (!start) return 0;

    return Math.max(0, diffDays(new Date(), start));
  };

  const handleClick = (app) => {
    localStorage.setItem("selectedAppartement", JSON.stringify(app));
    window.location.href = "/appartement";
  };

  const groupByFloor = () => {
    const floors = {};

    appartements.forEach(app => {
      if (
        modeUrgence &&
        !isUrgentJ1(app) &&
        !isUrgentToday(app) &&
        !isExpired(app)
      ) return;

      const numeroBase = app.numero.split("-")[0];

      let floor;

      if (numeroBase.startsWith("A10")) {
        // Nouveau format : A1001, A1012, A1032...
        const ancienNumero = numeroBase.substring(3); // ex. "32", "01", "42"
        floor = parseInt(ancienNumero.charAt(0), 10);
      } else {
        // Ancien format : A01, A12, A32...
        floor = parseInt(numeroBase.substring(1, 2), 10);
      }

      if (!floors[floor]) floors[floor] = [];
      floors[floor].push(app);
    });

    return floors;
  };

  const floors = groupByFloor();

  const urgentCount = appartements.filter(
    (a) => isUrgentJ1(a) || isUrgentToday(a)
  ).length;
  const expiredCount = appartements.filter(isExpired).length;

  return (
    <div className="p-4 bg-gray-100 min-h-screen">

      <div className="fixed top-1/2 right-4 -translate-y-1/2 space-y-2 z-50">
        {alerts.slice(-5).map(alert => (
          <div key={alert.id} className="bg-red-700 text-white px-4 py-2 rounded shadow animate-bounce">
            {alert.message}
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-extrabold tracking-wide">
          Gestion des Appartements
        </h2>

        <div className="flex flex-col items-end gap-2">

          <div className="flex gap-3 items-center">
            <button
              onClick={() => setModeUrgence(!modeUrgence)}
              className={`px-3 py-1 rounded text-white ${modeUrgence ? "bg-red-600" : "bg-gray-600"}`}
            >
              {modeUrgence ? "Mode Urgences" : "Mode Normal"}
            </button>

            <div className="bg-red-500 text-white px-2 py-1 rounded text-sm font-semibold">
              {urgentCount} urgences
            </div>

            <div className="bg-black text-white px-2 py-1 rounded text-sm font-semibold animate-pulse">
              {expiredCount} expirés
            </div>

            <div className="bg-green-600 text-white px-3 py-1 rounded animate-pulse font-semibold">
              LIVE
            </div>
          </div>

          <button
            onClick={() => {
              window.location.href = "/sejours";
            }}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg font-semibold shadow"
          >
            📋 Voir les séjours
          </button>

        </div>
      </div>

      <div className="flex justify-center gap-6 mb-6 flex-wrap text-sm font-medium">
        <span className="text-cyan-600">🔵 Chambre</span>
        <span className="text-purple-600">🟣 Premium</span>
        <span className="text-green-600">🟢 Standard</span>
        <span className="text-yellow-500">🟠 Réservé</span>
        <span className="text-red-600">🔴 Occupé</span>
        <span className="text-black">⚫ Expiré</span>
      </div>

      {Object.keys(floors)
        .sort((a, b) => b - a)
        .map(floor => (
          <div key={floor} className="mb-10">

            <h3 className="text-center font-semibold mb-4 text-lg">
              {floor == 0 ? "Rez-de-chaussée" : `Étage ${floor}`}
            </h3>

            <div className="flex justify-center gap-6 flex-wrap">

              {floors[floor].map(app => {
                const duree = getDuree(app.date_debut);
                const urgentJ1 = isUrgentJ1(app);
                const urgentToday = isUrgentToday(app);
                const expired = isExpired(app);
                const remaining = getRemainingToNoon();

                return (
                  <div
                    key={app.id}
                    onClick={() => handleClick(app)}
                    onMouseEnter={() => setHovered(app.id)}
                    onMouseLeave={() => {
                      setTimeout(() => {
                        setHovered(prev => (prev === app.id ? null : prev));
                      }, 350);
                    }}
                    
                                        className={`relative w-20 h-20 flex flex-col items-center justify-center text-white font-bold rounded-xl cursor-pointer
                      ${getColor(app)}
                      ${hovered === app.id ? "scale-110 shadow-2xl z-40" : "shadow"}
                      ${urgentJ1 ? "animate-pulse ring-4 ring-orange-400" : ""}
                      ${urgentToday ? "animate-pulse ring-4 ring-red-600" : ""}
                      ${expired ? "animate-bounce ring-4 ring-black" : ""}
                    `}
                  >
                    <div className="text-lg font-bold">
                      {app.numero}
                    </div>

                    <div className="text-[9px] uppercase font-semibold mt-1 bg-white/20 px-1 rounded">
                      {app.type_logement === "chambre"
                        ? "CH"
                        : app.type_logement === "appartement premium"
                        ? "PRM"
                        : app.type_logement === "appartement standard"
                        ? "STD"
                        : app.type_logement === "suite"
                        ? "STE"
                        : ""}
                    </div>

                    {hovered === app.id && (
                      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 w-64 bg-black text-white border border-gray-700 rounded-xl shadow-2xl p-4 z-50 animate-fade-in transition-all duration-200 hover:scale-[1.02]">

                        <div className="font-bold text-lg mb-2">
                          {app.type_logement === "chambre"
                            ? "Chambre"
                            : app.type_logement === "suite"
                            ? "Suite"
                            : "Appartement"}{" "}
                          {app.numero}
                        </div>

                        <div className="text-xs uppercase bg-white/10 inline-block px-2 py-1 rounded mt-1">
                          {app.type_logement}
                        </div>

                        <div className="text-sm space-y-1">
                          <div>Statut : <span className="font-semibold">{getEtat(app)}</span></div>

                          {app.client_nom && (
                            <div>Client : <span className="font-semibold">{app.client_nom}</span></div>
                          )}

                          {app.date_debut && (
                            <div>
                              Début : {toDate(app.date_debut)?.toLocaleDateString("fr-FR")}
                            </div>
                          )}

                          {app.date_fin_prevue && (
                            <div>
                              Fin : {toDate(app.date_fin_prevue)?.toLocaleDateString("fr-FR")}
                            </div>
                          )}

                          <div>Durée : {duree} jour(s)</div>

                          {app.reste_a_payer > 0 && (
                            <div className="text-red-600 font-semibold">
                              Reste : {app.reste_a_payer} $
                            </div>
                          )}

                          {expired && (
                            <div className="text-black font-bold">
                              ⚫ Séjour expiré
                            </div>
                          )}

                          {urgentToday && (
                            <div className="text-red-600 font-bold">
                              🚨 Urgent aujourd'hui
                            </div>
                          )}

                          {urgentJ1 && (
                            <div className="text-orange-500 font-bold">
                              ⚠️ Urgent J-1
                            </div>
                          )}
                        </div>

                      </div>
                    )}

                    {(showCheckin(app) || showCheckoutActions(app)) && remaining && (
                      <div className="absolute top-1 left-1 bg-black text-white text-[9px] px-1 rounded">
                        {remaining}
                      </div>
                    )}

                    {app.reste_a_payer > 0 && (
                      <div className="absolute top-1 right-1 bg-yellow-300 text-black text-xs px-1 rounded">
                        $
                      </div>
                    )}

                    {expired && (
                      <div className="absolute bottom-1 text-[10px] bg-black px-1 rounded">
                        EXP
                      </div>
                    )}

                  </div>
                );
              })}

            </div>
          </div>
        ))}

    </div>
  );
}

export default AppartementPlan;
